import React, { useEffect, useState } from "react";
import { Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import ConnectedProjectView from "../../components/ConnectedProjectView";
import axios from "axios";
import SearchComponent from "../../components/SearchComponent";
import NotificationIcon from "../../components/Notification";
import { useUser } from "../../contextProviders/userContext";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import { API_BASE_URL } from "../../api";

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useUser();

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/participations/my-projects`,
        {
          headers: getAuthHeader(),
        }
      );

      if (response.data?.success) {
        setProjects(response.data.data || []);
      } else {
        // If the response has projects in the old format
        const validProjects = (response.data || []).filter(
          (project) => project !== null
        );
        setProjects(validProjects);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError(
        error.response?.data?.error ||
          "Failed to fetch projects. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    const refreshProjects = () => fetchProjects();
    window.addEventListener("wcco:projects-updated", refreshProjects);
    return () => window.removeEventListener("wcco:projects-updated", refreshProjects);
  }, []);

  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Filtering logic with null checks
  const filteredProjects = projects.filter((project) => {
    if (!project) return false;

    if (searchTerm.trim() === "") return true;

    const lowerCaseTerm = searchTerm.toLowerCase();
    const searchFields = [
      project.name,
      project.description,
      project.organiserName,
      project.category,
      project.organiserType,
    ];

    return searchFields.some(
      (field) => field && field.toLowerCase().includes(lowerCaseTerm)
    );
  });

  const renderContent = () => {
    if (loading) {
      return (
        <Col className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Col>
      );
    }

    if (error) {
      return (
        <Col>
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        </Col>
      );
    }

    if (filteredProjects.length === 0) {
      return (
        <Col>
          <Alert variant="info" className="mt-3">
            {searchTerm.trim() !== ""
              ? "No projects found matching your search."
              : "You haven't joined any projects yet."}
          </Alert>
        </Col>
      );
    }

    return <ConnectedProjectView projects={filteredProjects} />;
  };

  return (
    <div
      className="d-flex flex-column flex-lg-row"
      style={{ maxWidth: "100vw", overflowX: "hidden" }}>
      <Sidebar />
      <Container
        fluid
        className="py-2"
        style={{
          minHeight: "100vh",
          maxHeight: "100vh",
          overflowY: "auto",
          backgroundColor: "var(--color-bg)",
        }}>
        <TopNavBar />

        {/* Header Section */}
        <Row
          className="align-items-center justify-content-between mx-0"
          style={{ padding: "1rem 0.5rem" }}>
          <Col xs={12} md={6} lg={4} className="mb-3 mb-md-0">
            <div className="d-flex flex-column text-start">
              <h4 className="mb-0 fw-bold" style={{ color: "var(--color-text)", letterSpacing: "-0.3px" }}>
                Hi {user?.name || "User"}
              </h4>
              <small style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>Here are your projects</small>
            </div>
          </Col>

          <Col xs={12} md={6} lg={8}>
            <div className="d-flex align-items-center justify-content-md-end">
              <div className="flex-grow-1 flex-md-grow-0">
                <SearchComponent
                  onSearchChange={handleSearchChange}
                  placeholder="Search projects..."
                />
              </div>
              <div className="mx-3 d-none d-md-block">
                <div
                  style={{
                    width: "1px",
                    height: "24px",
                    backgroundColor: "var(--color-border)",
                  }}
                />
              </div>
              <div>
                <NotificationIcon />
              </div>
            </div>
          </Col>
        </Row>

        <div className="px-3">
          <hr style={{ borderColor: "var(--color-border)", margin: "0.5rem 0 1.5rem" }} />
        </div>

        {/* Projects Section */}
        <Row className="mx-0">{renderContent()}</Row>
      </Container>
    </div>
  );
};

export default MyProjects;
